import { STORAGE, STORAGE_KEY } from "@/constants";
import { useGroupProvider } from "@/context/group-provider";
import React, { useEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getItem } from "@/lib/storage-utils";
import ScreenRouteNames from "@/constants/screen-route-names";

type AccessConfig = {
  hasGroupId: boolean;
  hasAgreed: boolean;
};

type RouteConfig = {
  allowed: (config: AccessConfig) => boolean;
  redirectTo: (config: AccessConfig) => string;
};

const RequireGroup = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { readCachedGroupDetail } = useGroupProvider();
  const currentPath = location.pathname.split("/")[1] || "";

  // sync groupData on route change
  useEffect(() => {
    readCachedGroupDetail();
  }, [location.pathname, readCachedGroupDetail]);

  const config: AccessConfig = useMemo(() => {
    const cachedGroupDetail = getItem(
      STORAGE_KEY.CACHED_GROUP_DETAIL,
      STORAGE.LOCAL,
    );
    const sessionGroupData = getItem(STORAGE_KEY.GROUP_DATA, STORAGE.SESSION);

    return {
      hasGroupId:
        Boolean(cachedGroupDetail?.groupId) && Boolean(sessionGroupData),
      hasAgreed:
        Boolean(cachedGroupDetail?.agreement) &&
        Boolean(cachedGroupDetail?.groupId),
    };
  }, [location.pathname]);

  const routeConfigs: Record<string, RouteConfig> = useMemo(() => {
    const {
      LOGIN,
      DISCLAIMER,
      REGISTER,
      ACCOUNT_RESET,
      VERIFICATION,
      VERIFY_OTP,
    } = ScreenRouteNames;

    function repeatedStandardRoute(): RouteConfig {
      return {
        allowed: ({ hasGroupId, hasAgreed }) => hasGroupId && hasAgreed,
        redirectTo: ({ hasGroupId, hasAgreed }) => {
          if (!hasGroupId) return "/";
          if (!hasAgreed) return `/${DISCLAIMER.path}`;
          return "/";
        },
      };
    }

    return {
      "": {
        allowed: ({ hasGroupId }) => !hasGroupId,
        redirectTo: ({ hasGroupId, hasAgreed }) => {
          if (hasGroupId && hasAgreed) return `/${LOGIN.path}`;
          if (hasGroupId && !hasAgreed) return `/${DISCLAIMER.path}`;
          return "/";
        },
      },
      [DISCLAIMER.path]: {
        allowed: ({ hasGroupId, hasAgreed }) => hasGroupId && !hasAgreed,
        redirectTo: ({ hasGroupId, hasAgreed }) => {
          if (!hasGroupId) return "/";
          if (hasAgreed) return `/${LOGIN.path}`;
          return "/";
        },
      },
      [LOGIN.path]: repeatedStandardRoute(),
      [REGISTER.path]: repeatedStandardRoute(),
      [ACCOUNT_RESET.path]: repeatedStandardRoute(),
      [VERIFICATION.path]: repeatedStandardRoute(),
      [VERIFY_OTP.path]: repeatedStandardRoute(),
    };
  }, []);

  function getRedirectRoute(): string | null {
    const routeConfig = routeConfigs[currentPath];

    if (!routeConfig) {
      return null;
    }

    // for VERIFY_OTP - check location.state first
    if (currentPath === ScreenRouteNames.VERIFY_OTP.path) {
      if (!location.state?.registerInfo) {
        return `/${ScreenRouteNames.LOGIN.path}`;
      }
    }

    if (!routeConfig.allowed(config)) {
      return routeConfig.redirectTo(config);
    }

    return null;
  }

  const redirectTo = getRedirectRoute();

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default RequireGroup;
